import React from 'react';
import { View } from '@react-pdf/renderer';
import { appNames, reportingStandards } from '../../constants';
import { isBoolean } from '../../utils/validations';
import { Column, Label, Row, SectionHeader, SectionTitle, Value } from '../components';
import { format } from '../../utils/locale';

const EligibilityStandards = ({ data, type, reportingStandard }) => {
  const eligibilityStandards = data || {};
  const isOTCQXUS = type === appNames.OTCQX_US;
  const isOTCQXBanks = type === appNames.OTCQX_BANKS;
  const isOTCQXIntl = type === appNames.OTCQX_INTL;
  const isOTCQB = type === appNames.OTCQB;
  const isDNS = type === appNames.DNS;
  const isAlternativeReportStandard = reportingStandard === reportingStandards.ALTERNATIVE;

  return (
    <>
      <View fixed>
        <SectionHeader text='ELIGIBILITY STANDARDS' />
      </View>
      <View>
        <SectionTitle text='AUDITED FINANCIAL STATEMENTS' />
        {(isOTCQXUS || isOTCQXBanks || isOTCQXIntl || isOTCQB) && <Row>
          <Column size='75'>
            <Label text='Question' />
            <Value text={
              'Does the company have audited annual financial statements that meet the criteria below?\n' +
              '• US company financials must be audited by a PCAOB registered auditor and prepared in accordance to US GAAP.\n' +
              '• Non-US company financials must be prepared in accordance with US GAAP, IFRS or an IFRS equivalent standard.'
            } />
          </Column>
          <Column size='25'>
            <Label text='Answer' />
            <Value text={format(eligibilityStandards.auditedFinancials, null, 'bool')} />
          </Column>
        </Row>}
        {isBoolean(eligibilityStandards.auditedFinancials) && !eligibilityStandards.auditedFinancials && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.auditedFinancialsDetail} />
          </Column>
        </Row>}
        <SectionTitle text='SHELL OR A BLANK CHECK COMPANY' />
        <Row>
          <Column size='75'>
            <Label text='Question' />
            <Value text='Is the company a shell or a blank check company?' />
          </Column>
          <Column size='25'>
            <Label text='Answer' />
            <Value text={format(eligibilityStandards.isShellBlankCheckCompany, null, 'bool')} />
          </Column>
        </Row>
        {(isOTCQXUS || isOTCQXBanks || isOTCQXIntl) && eligibilityStandards.isShellBlankCheckCompany && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.isShellBlankCheckCompanyDetail} />
          </Column>
        </Row>}
        <SectionTitle text='IN GOOD STANDING' />
        <Row>
          <Column size='75'>
            <Label text='Question' />
            <Value text='Is the company in good standing under the laws of each jurisdiction in which the company is organized or does business?' />
          </Column>
          <Column size='25'>
            <Label text='Answer' />
            <Value text={format(eligibilityStandards.inGoodStanding, null, 'bool')} />
          </Column>
        </Row>
        {isBoolean(eligibilityStandards.inGoodStanding) && !eligibilityStandards.inGoodStanding && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.inGoodStandingDetail} />
          </Column>
        </Row>}
        <SectionTitle text='BANKRUPTCY' />
        <Row>
          <Column size='75'>
            <Label text='Question' />
            <Value text='Is the company subject to any Bankruptcy or reorganization proceedings?' />
          </Column>
          <Column size='25'>
            <Label text='Answer' />
            <Value text={format(eligibilityStandards.isBankrupt, null, 'bool')} />
          </Column>
        </Row>
        {eligibilityStandards.isBankrupt && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.isBankruptDetail} />
          </Column>
        </Row>}
        {!isOTCQXIntl && <>
          <SectionTitle text='Independent Directors' />
          <Row>
            <Column size='75'>
              <Label text='Question' />
              <Value text='Does the company have a Board of Directors that includes at least two (2) Independent Directors?' />
            </Column>
            <Column size='25'>
              <Label text='Answer' />
              <Value text={format(eligibilityStandards.hasTwoIndependentDirectors, null, 'bool')} />
            </Column>
          </Row>
        </>}
        {(isOTCQXUS || isOTCQXBanks || (isOTCQB && isAlternativeReportStandard)) && isBoolean(eligibilityStandards.hasTwoIndependentDirectors) && !eligibilityStandards.hasTwoIndependentDirectors && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.hasTwoIndependentDirectorsDetail} />
          </Column>
        </Row>}
        {!isOTCQXIntl && <>
          <SectionTitle text='Audit Committee' />
          <Row>
            <Column size='75'>
              <Label text='Question' />
              <Value text='Does the company have an audit committee, a majority of whose members are Independent Directors?' />
            </Column>
            <Column size='25'>
              <Label text='Answer' />
              <Value text={format(eligibilityStandards.hasAuditComittee, null, 'bool')} />
            </Column>
          </Row>
        </>}
        {(isOTCQXUS || isOTCQXBanks || (isOTCQB && isAlternativeReportStandard)) && isBoolean(eligibilityStandards.hasAuditComittee) && !eligibilityStandards.hasAuditComittee && <Row>
          <Column size='100'>
            <Label text='DESCRIPTION' />
            <Value text={eligibilityStandards.hasAuditComitteeDetail} />
          </Column>
        </Row>}
        {(isOTCQXUS || isOTCQXBanks) && <>
          <SectionTitle text={`ANNUAL SHAREHOLDERS' MEETINGS`} />
          <Row>
            <Column size='75'>
              <Label text='Question' />
              <Value text='Does the company conduct Annual Shareholders’ Meetings and make Annual Financial Reports available to its shareholders at least 15 calendar days prior to such meetings?' />
            </Column>
            <Column size='25'>
              <Label text='Answer' />
              <Value text={format(eligibilityStandards.hasShareholderMeetings, null, 'bool')} />
            </Column>
          </Row>
          {isBoolean(eligibilityStandards.hasShareholderMeetings) && !eligibilityStandards.hasShareholderMeetings && <Row>
            <Column size='100'>
              <Label text='DESCRIPTION' />
              <Value text={eligibilityStandards.hasShareholderMeetingsDetail} />
            </Column>
          </Row>}
        </>}
      </View>
    </>
  );
};

export default EligibilityStandards;
